import { ThemeContext } from '@tracsis/library/src/context/ThemeContext';
import cx from 'classnames';
import { Link } from 'gatsby';
import React, { useContext, useEffect, useState } from 'react';
import {
	Accordion,
	AccordionItem,
	AccordionItemButton,
	AccordionItemHeading,
	AccordionItemPanel,
} from 'react-accessible-accordion';

import { MegaMenu, MobileMenu } from '../../../index';
import { ChevronDown, Cross, Toggle } from '../../Icon';
import NavigationPanel from '../../Navigation/NavigationPanel';
import SocketNavigation from '../../Navigation/SocketNavigation';
import { IInnotransNavLink, INavigationItem } from '../Layout/Layout';
import * as styles from './Header.module.css';

const Header = ({
	siteName,
	logo,
	navigation,
	innotransnav,
	subNavigation,
	careers,
	isGroup = false,
	isCampaign = false,
}: {
	siteName: string;
	logo: React.ReactNode;
	navigation: INavigationItem[];
	innotransnav: IInnotransNavLink[];
	subNavigation: any;
	careers: boolean;
	isGroup?: boolean;
	isCampaign?: boolean;
}) => {
	const { variant, mobileMenuContext, setMobileMenuContext } =
		useContext(ThemeContext);
	const [accordionOpen, setAccordionOpen] = useState(false);
	const [megaMenuOpen, setMegaMenuOpen] = useState(-1);

	const isBrowser = typeof window !== 'undefined';

	if (isBrowser) {
		mobileMenuContext
			? document.body.classList.add('lock-mobile')
			: document.body.classList.remove('lock-mobile');
	}

	if (isBrowser) {
		accordionOpen
			? document.body.classList.add('lock-accordion')
			: document.body.classList.remove('lock-accordion');
	}

	const handleMegaMenuClick = (
		e: React.MouseEvent<HTMLElement>,
		key: number
	) => {
		e.preventDefault();

		setAccordionOpen(false);
		if (megaMenuOpen === key) {
			setMegaMenuOpen(-1);
			return;
		}

		setMegaMenuOpen(key);
	};

	return (
		<header className={styles.header} id="header">
			<div className={styles.background}>
				{!isGroup && navigation && (
					<SocketNavigation navigation={navigation} careers={careers} />
				)}

				<div className={styles.layout}>
					{logo && (
						<div className={cx(styles.logo, styles[variant])}>
							<Link to="/" aria-label={`${siteName} logo`}>
								{logo}
							</Link>
						</div>
					)}

					{isGroup && navigation && !isCampaign && (
						<nav className={styles.navigation}>
							<ul className={cx(styles.list, styles.companies)}>
								{navigation.map(({ label, globalNavigation }, key) => {
									if (!globalNavigation) return;

									return (
										<li key={key}>
											<Accordion
												allowZeroExpanded
												onChange={() => {
													setMegaMenuOpen(-1);
													setAccordionOpen(!accordionOpen);
												}}>
												<AccordionItem dangerouslySetExpanded={accordionOpen}>
													<AccordionItemHeading>
														<AccordionItemButton className={styles.button}>
															<div className={styles.toggleIcon}>
																<Toggle />
															</div>
															<div className={styles.crossIcon}>
																<Cross />
															</div>
															{label}
														</AccordionItemButton>
													</AccordionItemHeading>
													<AccordionItemPanel className={styles.overlay}>
														<NavigationPanel
															subMenus={globalNavigation}
															accordionOpen={accordionOpen}
														/>
													</AccordionItemPanel>
												</AccordionItem>
											</Accordion>
										</li>
									);
								})}
							</ul>

							<ul className={styles.list}>
								{navigation.map(
									(
										{
											to,
											label,
											links,
											description,
											globalNavigation,
											subMenu,
										},
										key
									) => {
										if (globalNavigation) return;

										return (
											to &&
											(subMenu ? (
												<li
													className={cx(styles[variant], styles.item)}
													key={key}>
													<Link
														to={to}
														onClick={(e) => handleMegaMenuClick(e, key)}
														className={cx(styles.link, {
															[styles.active]: megaMenuOpen === key,
														})}
														activeClassName={cx({
															[styles.current]: megaMenuOpen === -1,
														})}
														partiallyActive={true}>
														{label}
														<ChevronDown className={styles.chevron} />
													</Link>

													{links && subMenu && (
														<MegaMenu
															links={links}
															menu={subMenu}
															open={megaMenuOpen === key ? true : false}
															setOpen={setMegaMenuOpen}>
															<h3>{label}</h3>
															<p>{description}</p>
														</MegaMenu>
													)}
												</li>
											) : (
												<li
													className={cx(styles[variant], styles.item)}
													key={key}>
													<Link
														to={to}
														className={cx(styles.link, {
															[styles.active]: megaMenuOpen === key,
														})}
														activeClassName={cx({
															[styles.active]: megaMenuOpen === -1,
														})}
														partiallyActive={true}>
														{label}
													</Link>
												</li>
											))
										);
									}
								)}
							</ul>
						</nav>
					)}

					{!isGroup && navigation && (
						<ul className={styles.list}>
							{navigation.map(
								(
									{
										to,
										label,
										hilite,
										links,
										description,
										globalNavigation,
										subMenu,
									},
									key
								) => {
									if (globalNavigation) return;

									return (
										to &&
										(subMenu ? (
											<li
												className={cx(styles[variant], styles.item)}
												key={key}>
												<Link
													to={to}
													onClick={(e) => handleMegaMenuClick(e, key)}
													className={cx(styles.link, {
														[styles.active]: megaMenuOpen === key,
													})}
													activeClassName={cx({
														[styles.current]: megaMenuOpen === -1,
													})}
													partiallyActive={true}>
													{label}
													<ChevronDown className={styles.chevron} />
												</Link>

												{links && subMenu && (
													<MegaMenu
														links={links}
														menu={subMenu}
														open={megaMenuOpen === key ? true : false}
														setOpen={setMegaMenuOpen}>
														<h3>{label}</h3>
														<p>{description}</p>
													</MegaMenu>
												)}
											</li>
										) : (
											<li
												className={cx(styles[variant], styles.item, {
													[styles.hilite]: hilite,
												})}
												key={key}>
												<Link
													to={to}
													className={styles.link}
													activeClassName={cx({
														[styles.active]: megaMenuOpen === -1,
													})}
													partiallyActive={true}
													onClick={(e) => setMegaMenuOpen(-1)}>
													{label}
												</Link>
											</li>
										))
									);
								}
							)}
						</ul>
					)}

					{/** innotrans navigation  */}

					{isCampaign && (
						<nav className={styles.innotransNav}>
							<ul className={styles.innotransList}>
								{innotransnav.map(
									({ to, label, description, subMenu, links }, key) => {
										return (
											to && (
												<>
													<li className={styles.link} key={key}>
														<Link to={to} className={styles.hubLink}>
															Innotrans 2024
														</Link>
													</li>
													<li
														className={cx(styles[variant], styles.item)}
														key={key}>
														<Link
															to={to}
															onClick={(e) => handleMegaMenuClick(e, key)}
															className={cx(styles.innotransLink, {
																[styles.active]: megaMenuOpen === key,
															})}
															partiallyActive={true}>
															<Toggle className={styles.menu} />
														</Link>

														{links && subMenu && (
															<MegaMenu
																links={links}
																menu={subMenu}
																open={megaMenuOpen === key ? true : false}
																setOpen={setMegaMenuOpen}
																isCampaign>
																<h3>{label}</h3>
																<p>{description}</p>
															</MegaMenu>
														)}
													</li>
												</>
											)
										);
									}
								)}
							</ul>
						</nav>
					)}
					{/** end of innotrans  navigation */}

					<button
						className={styles.toggle}
						aria-label="Open the menu"
						onClick={() => setMobileMenuContext(true)}>
						<div className={styles.toggleIcon}>
							<Toggle />
						</div>
					</button>
				</div>

				<MobileMenu
					logo={logo}
					isGroup={isGroup}
					navigation={navigation}
					subNavigation={subNavigation}
					innotransnav={innotransnav}
					isCampaign={isCampaign}
					careers={careers}
					open={mobileMenuContext}
					setOpen={setMobileMenuContext}
				/>
			</div>
		</header>
	);
};

export default Header;
